"use client";

import { useEffect, useMemo, useState } from "react";
import { useTranslations } from "next-intl";
import {
  Autocomplete,
  Button,
  Collection,
  Header,
  Input,
  ListBox as RACListBox,
  ListBoxItem,
  ListBoxSection,
  Popover,
  Select as RACSelect,
  SearchField,
  Tabs,
  type Key,
} from "react-aria-components";
import { useFilter } from "react-aria";
import clsx from "clsx";

import { logger } from "@utilities/logger";
import IconChevronDownSmall from "@ui/central-icons/icons/IconChevronDownSmall";
import IconCheckmark2Small from "@ui/central-icons/icons/IconCheckmark2Small";
import IconMagnifyingGlass2 from "@ui/central-icons/icons/IconMagnifyingGlass2";
import { iconStrokeFix } from "@ui/central-icons/iconUtils";
import { TabList } from "@ui/components/core/TabList";
import { calculateLlmPrices, type LlmPriceEntry } from "@/app/_api/elevenlabs/llmPrice";
import { formatPricePerMinute } from "@utilities/numbers";

import { type CalculatorMode, usePricingTableContext } from "../PricingTableContext";
import { BillingCycleToggle } from "../BillingCycleToggle";
import { UsageSlider } from "./UsageSlider";
import { getAgentsMaxIncludedMinutes, useUsageSlider } from "./calculatorUtils";
import { featuredByPrice, groupedByProvider, modelLabel, pickDefaultModel } from "./agentModels";
import { StepIndicator } from "./StepIndicator";
import { AgentsCalculatorResultCard } from "./AgentsCalculatorResultCard";

const DEFAULT_MINUTES_PER_MONTH = 270;

interface ModelSelectProps {
  prices: LlmPriceEntry[];
  selectedModel: string;
  onSelect: (model: string) => void;
  ariaLabel: string;
  placeholder: string;
  searchPlaceholder: string;
  emptyLabel: string;
  perMinuteSuffix: string;
}

/**
 * React Aria single-select over the priced models — grouped by provider with each model's LLM $/min
 * shown inline, plus a type-to-filter search box pinned at the top of the popover (mirrors the in-app
 * LLM selector). The trigger keeps showing the chosen model + its price while the user searches: its
 * value is resolved from the full price list, independent of the (filtered) listbox. Built on raw RAC
 * primitives — Select + Autocomplete + SearchField + grouped ListBox — since the core ListBox owns its
 * own popover and can't host a search field inside it.
 */
function ModelSelect({
  prices,
  selectedModel,
  onSelect,
  ariaLabel,
  placeholder,
  searchPlaceholder,
  emptyLabel,
  perMinuteSuffix,
}: ModelSelectProps) {
  const { contains } = useFilter({ sensitivity: "base" });
  const groups = useMemo(() => groupedByProvider(prices), [prices]);

  const selected = prices.find(p => p.llm === selectedModel);
  const selectedPrice = selected ? `$${formatPricePerMinute(selected.pricePerMinute)}` : undefined;

  return (
    <RACSelect
      aria-label={ariaLabel}
      selectedKey={selectedModel || null}
      onSelectionChange={(key: Key | null) => {
        if (key !== null) onSelect(String(key));
      }}
    >
      <Button
        className={({ isFocusVisible }) =>
          clsx(
            "tw-flex tw-w-full tw-items-center tw-justify-between tw-gap-2 tw-h-12 tw-px-4 tw-bg-white tw-border tw-border-gray-200 tw-rounded-xl tw-type-base tw-text-black tw-transition-colors hover:tw-border-gray-300 tw-cursor-pointer",
            isFocusVisible ? "tw-outline-focus" : "tw-outline-none",
          )
        }
      >
        <span className='tw-flex-1 tw-min-w-0 tw-truncate tw-text-left'>
          {selected ? modelLabel(selected.llm) : placeholder}
        </span>
        <span className='tw-flex tw-flex-none tw-items-center tw-gap-2'>
          {selectedPrice && (
            <span className='tw-tabular-nums tw-text-gray-500'>
              {selectedPrice}
              {perMinuteSuffix}
            </span>
          )}
          <IconChevronDownSmall className={clsx("tw-size-4 tw-text-gray-400 tw-flex-none", iconStrokeFix)} />
        </span>
      </Button>
      <Popover
        offset={8}
        placement='bottom'
        className='tw-w-[--trigger-width] tw-flex tw-flex-col tw-overflow-hidden tw-rounded-2xl tw-bg-white tw-text-sm tw-text-black tw-transition tw-ease-out tw-duration-150 data-[entering]:tw-opacity-0 data-[exiting]:tw-opacity-0'
        style={{ boxShadow: "0 0 1px 0 rgb(0 0 0 / 0.4), 0 4px 4px rgb(0 0 0 / 0.04)" }}
      >
        <Autocomplete filter={contains}>
          <SearchField
            aria-label={searchPlaceholder}
            className='tw-flex tw-h-12 tw-flex-none tw-items-center tw-gap-2 tw-border-b tw-border-gray-100 tw-px-4'
          >
            <IconMagnifyingGlass2
              aria-hidden
              className={clsx("tw-size-4 tw-flex-none tw-text-gray-400", iconStrokeFix)}
            />
            <Input
              placeholder={searchPlaceholder}
              className='tw-flex-1 tw-min-w-0 tw-bg-transparent tw-outline-none tw-text-black placeholder:tw-text-gray-400 [&::-webkit-search-cancel-button]:tw-hidden'
            />
          </SearchField>
          <RACListBox
            aria-label={ariaLabel}
            selectionMode='single'
            className='tw-max-h-64 tw-flex-auto tw-overflow-auto tw-scrollbar-none tw-scroll-py-1.5 tw-p-1.5 tw-outline-none'
            renderEmptyState={() => <div className='tw-px-2.5 tw-py-3 tw-text-gray-500'>{emptyLabel}</div>}
          >
            {groups.map((group, groupIndex) => (
              <ListBoxSection key={group.id} id={group.id}>
                <Header
                  className={clsx(
                    "tw-px-2.5 tw-py-1 tw-text-xs tw-font-medium tw-text-gray-500",
                    groupIndex > 0 && "tw-mt-2",
                  )}
                >
                  {group.label}
                </Header>
                <Collection items={group.models}>
                  {model => (
                    <ListBoxItem
                      id={model.llm}
                      textValue={modelLabel(model.llm)}
                      className={({ isHovered, isFocusVisible }) =>
                        clsx(
                          "tw-flex tw-h-9 tw-items-center tw-justify-between tw-gap-4 tw-rounded-xl tw-px-2.5 tw-cursor-pointer tw-outline-none",
                          (isHovered || isFocusVisible) && "tw-bg-gray-50",
                          isFocusVisible && "tw-outline-focus",
                        )
                      }
                    >
                      {({ isSelected }) => (
                        <>
                          <span className='tw-truncate'>{modelLabel(model.llm)}</span>
                          <span className='tw-flex tw-flex-none tw-items-center tw-gap-1.5'>
                            <span className='tw-tabular-nums tw-text-gray-500'>
                              ${formatPricePerMinute(model.pricePerMinute)}
                              {perMinuteSuffix}
                            </span>
                            <IconCheckmark2Small
                              className={clsx("tw-size-4 tw-text-black", iconStrokeFix, !isSelected && "tw-invisible")}
                            />
                          </span>
                        </>
                      )}
                    </ListBoxItem>
                  )}
                </Collection>
              </ListBoxSection>
            ))}
          </RACListBox>
        </Autocomplete>
      </Popover>
    </RACSelect>
  );
}

function FieldLabel({ children }: { children: React.ReactNode }) {
  return <label className='tw-type-sm tw-font-medium tw-block tw-text-black'>{children}</label>;
}

export interface AgentsCalculatorProps {
  /** Server-fetched prices for the default scenario; seed the first client paint (component is `ssr: false`). */
  baselinePrices: LlmPriceEntry[];
  className?: string;
}

/**
 * Estimates the LLM cost of an ElevenAgents conversation. The user picks a model and the monthly
 * conversation minutes; the per-minute LLM price (USD) for every model comes from the public agents
 * pricing endpoint via the {@link calculateLlmPrices} server action. The endpoint returns all models
 * in one call for the baseline scenario, so model selection is pure client-side and changing minutes
 * only scales the result client-side — no input re-fetches.
 *
 * The result card mirrors the API calculator: "subscription" recommends the cheapest plan covering
 * the estimated minutes (with LLM cost as an add-on); "pay as you go" shows the estimated monthly
 * LLM cost. The payment-model + billing-cycle state is shared via {@link usePricingTableContext}.
 */
export function AgentsCalculator({ baselinePrices, className }: AgentsCalculatorProps) {
  const t = useTranslations("PricingClientKeys.AgentsCalculator");
  const { calculatorMode, setCalculatorMode, billingCycle } = usePricingTableContext();

  const [prices, setPrices] = useState<LlmPriceEntry[]>(baselinePrices);
  const [selectedModel, setSelectedModel] = useState<string>(() => pickDefaultModel(baselinePrices));
  // Seed loading when there's no baseline so the first client fetch shows progress, not an empty card.
  const [isLoading, setIsLoading] = useState(baselinePrices.length === 0);

  // Let the minutes slider reach ~1.5× the largest self-serve plan so dragging to the top
  // surfaces the Enterprise ("contact sales") recommendation.
  const minutesMax = useMemo(() => Math.max(1000, Math.ceil((getAgentsMaxIncludedMinutes() * 1.5) / 1000) * 1000), []);
  const minutesSlider = useUsageSlider(DEFAULT_MINUTES_PER_MONTH, minutesMax);

  // Self-heal: the prices are fixed (baseline scenario) so we only fetch when the SSR baseline was
  // empty — e.g. the endpoint was cold or unreachable at render time. Otherwise the server-provided
  // prices are used as-is and nothing re-fetches. `isLoading` already starts true in this case
  // (seeded from the empty baseline), so the effect only has to clear it once the fetch settles.
  useEffect(() => {
    if (baselinePrices.length > 0) return;

    let cancelled = false;
    calculateLlmPrices()
      .then(next => {
        if (cancelled || next.length === 0) return;
        setPrices(next);
        setSelectedModel(current => current || pickDefaultModel(next));
      })
      .catch(error => {
        logger.error(
          error instanceof Error ? error : null,
          "[AgentsCalculator] Failed to fetch LLM prices; rendering calculator empty",
        );
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [baselinePrices]);

  // Fall back to the cheapest featured model (not raw prices[0]) so the highlighted card always
  // matches a model the dropdown actually offers; prices[0] is a last resort if none are featured.
  const selectedEntry = prices.find(p => p.llm === selectedModel) ?? featuredByPrice(prices)[0] ?? prices[0];

  const tabItems = [
    { id: "subscription", title: t("tab_subscription") },
    { id: "payg", title: t("tab_payg") },
  ];

  return (
    <div className={className}>
      <div className='tw-flex tw-flex-col tw-pt-4 lg:tw-pt-0 lg:tw-flex-row tw-gap-8 lg:tw-gap-16'>
        {/* Inputs */}
        <div className='tw-w-full tw-space-y-8 tw-order-2 lg:tw-order-1 lg:tw-w-1/2'>
          <div>
            <StepIndicator step={1} label={t("step_payment_model")} />
            <div className='tw-flex tw-flex-wrap tw-items-center tw-gap-3'>
              <Tabs selectedKey={calculatorMode} onSelectionChange={key => setCalculatorMode(key as CalculatorMode)}>
                <TabList items={tabItems} surface='cream' size='md' aria-label={t("payment_model_aria_label")} />
              </Tabs>
              {calculatorMode === "subscription" && <BillingCycleToggle size='md' />}
            </div>
          </div>

          <div>
            <StepIndicator step={2} label={t("step_configure")} />
            <div className='tw-space-y-6'>
              {/* Conversation minutes drive the recommended plan / monthly cost. */}
              <div className='tw-space-y-3'>
                <FieldLabel>{t("minutes_label")}</FieldLabel>
                <UsageSlider
                  sliderValue={minutesSlider.sliderValue}
                  onSliderChange={minutesSlider.handleSliderChange}
                  maxValue={minutesMax}
                  unitSuffix={t("minutes_unit")}
                  ariaLabel={t("minutes_label")}
                />
              </div>

              <div className='tw-space-y-2'>
                <FieldLabel>{t("model_label")}</FieldLabel>
                <ModelSelect
                  prices={prices}
                  selectedModel={selectedModel}
                  onSelect={setSelectedModel}
                  ariaLabel={t("model_label")}
                  placeholder={t("model_placeholder")}
                  searchPlaceholder={t("model_search_placeholder")}
                  emptyLabel={t("model_search_empty")}
                  perMinuteSuffix={t("per_minute_suffix")}
                />
              </div>
            </div>

            <p className='tw-mt-4 tw-type-xs tw-text-gray-500'>{t("footnote")}</p>
          </div>
        </div>

        {/* Result */}
        <div className='tw-w-full tw-order-1 lg:tw-order-2 lg:tw-w-1/2'>
          <AgentsCalculatorResultCard
            pricePerMinute={selectedEntry?.pricePerMinute}
            modelName={selectedEntry ? modelLabel(selectedEntry.llm) : ""}
            minutesPerMonth={minutesSlider.value}
            mode={calculatorMode}
            period={billingCycle}
            isLoading={isLoading}
          />
        </div>
      </div>
    </div>
  );
}
